import { Table } from "antd";
import type { TableProps } from "antd/lib/table";
import classNames from "classnames";
import React from "react";
import ReactDragListView from "react-drag-listview";
import { Resizable } from "react-resizable";
import "./StandardTable.scss";

// Global variable để track resize state
const globalResizeState = {
  isResizing: false,
  timeoutId: null as NodeJS.Timeout | null
};

// Component ResizableTitle để handle resize columns
const ResizableTitle = (props: any) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  const handleResizeStart = () => {
    globalResizeState.isResizing = true;

    // Clear any existing timeout
    if (globalResizeState.timeoutId) {
      clearTimeout(globalResizeState.timeoutId);
    }
  };

  const handleResizeStop = (e: React.SyntheticEvent<Element, Event> | MouseEvent, data: any) => {

    if (onResize) {
      onResize(e, data);
    }

    // Set timeout to reset resize state
    globalResizeState.timeoutId = setTimeout(() => {
      globalResizeState.isResizing = false;
    }, 150); // Increased timeout to ensure mouseUp is handled
  };

  // Override onClick to check global resize state
  const headerProps = {
    ...restProps,
    onClick: (e: React.MouseEvent) => {

      // Prevent sort if ANY column is being resized
      if (globalResizeState.isResizing) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }

      // Call original onClick if exists
      if (restProps.onClick) {
        restProps.onClick(e);
      }
    }
  };

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        />
      }
      onResizeStart={handleResizeStart}
      onResize={(e, data) => {
        if (onResize) {
          onResize(e, data);
        }
      }}
      onResizeStop={handleResizeStop}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...headerProps} />
    </Resizable>
  );
};

export interface StandardTableCustomProps extends TableProps<any> {
  /**Option pass id of table */
  idContainer?: string;
  /**Table is draggable */
  isDragable?: boolean;
  /**Used to change style table */
  className?: string;
  /**Show sorter tooltip */
  showSorterTooltip?: boolean,
  /**Use to resize column*/
  colResizable?: boolean;
}

function StandardTable(props: StandardTableCustomProps) {
  const {
    className,
    isDragable,
    expandable,
    idContainer,
    showSorterTooltip = false,
    columns,
    colResizable = false,
    virtual = false,
  } = props;
  const [columnInternal, setColumnInternal] = React.useState<any>([...columns])

  const [checkExpand, setCheckExpand] = React.useState<boolean>(false);

  // Sync columnInternal with columns props when columns change
  React.useEffect(() => {
    setColumnInternal([...columns]);
  }, [columns]);


  React.useEffect(() => {
    if (typeof props.dataSource?.length === "number") {
      let antTableDOM: any;
      let antTableTbodyDOM: any;
      if (idContainer) {
        const containerDOM = document.getElementById(idContainer);
        antTableDOM = containerDOM.getElementsByClassName("ant-table-body")[0]
          || containerDOM.getElementsByClassName("ant-table-content")[0];
        antTableTbodyDOM = containerDOM.getElementsByClassName("ant-table-tbody")[0];
      } else {
        antTableDOM = document.getElementsByClassName("ant-table-body")[0] ||
          document.getElementsByClassName("ant-table-content")[0];
        antTableTbodyDOM = document.getElementsByClassName("ant-table-tbody")[0];
      }
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;

      if (checkExpand) {
        setCheckExpand(false);
      }

      const antTableBody = antTableDOM || antTableTbodyDOM;

      if (isDragable && antTableBody) {
        const handleMouseDown = (e: any) => {
          isDown = true;
          antTableBody.classList.add("active-draggable");
          startX = e.pageX - antTableBody.offsetLeft;
          scrollLeft = antTableBody.scrollLeft;
        };

        const handleMouseLeave = () => {
          isDown = false;
          antTableBody.classList.remove("active-draggable");
        };

        const handleMouseUp = () => {
          isDown = false;
          antTableBody.classList.remove("active-draggable");
        };

        const handleMouseMove = (e: any) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - antTableBody.offsetLeft;
          const walk = (x - startX) * 3;
          antTableBody.scrollLeft = scrollLeft - walk;
        };

        antTableBody.addEventListener("mousedown", handleMouseDown);

        antTableBody.addEventListener("mouseleave", handleMouseLeave);

        antTableBody.addEventListener("mouseup", handleMouseUp);

        antTableBody.addEventListener("mousemove", handleMouseMove);

        return () => {
          antTableBody.removeEventListener("mousedown", handleMouseDown);
          antTableBody.removeEventListener("mouseleave", handleMouseLeave);
          antTableBody.removeEventListener("mouseup", handleMouseUp);
          antTableBody.removeEventListener("mousemove", handleMouseMove);
        };
      }
    }

  }, [idContainer, isDragable, props.dataSource?.length, checkExpand]);



  const dragProps = {
    onDragEnd(fromIndex: number, toIndex: number) {
      const columnsTmp = [...columnInternal];
      const item = columnsTmp.splice(fromIndex, 1)[0];
      columnsTmp.splice(toIndex, 0, item);
      setColumnInternal(
        [...columnsTmp]
      );
    },
    nodeSelector: "th",
    handleSelector: ".dragHandler",
    ignoreSelector: "react-resizable-handle"
  };
  const components = {
    header: {
      cell: ResizableTitle
    }
  };


  const handleResize = React.useCallback((index: number) => {
    return (e: any, col: any) => {
      setColumnInternal((columns: any) => {
        const nextColumns = [...columns];
        nextColumns[index] = {
          ...nextColumns[index],
          width: col?.size?.width
        };
        return [...nextColumns];
      });
    }
  }, []);

  const newColumn = React.useMemo(() => {
    const newColumn = columnInternal.map((col: any, index: number) => ({
      ...col,
      onHeaderCell: colResizable ? (column: any) => ({
        width: column.width,
        onResize: handleResize(index),
        // Preserve original onHeaderCell if exists
        ...(col.onHeaderCell ? col.onHeaderCell(column) : {})
      }) : col.onHeaderCell
    }));
    return newColumn;
  }, [columnInternal, colResizable, handleResize]);



  return (
    <>
      <div className={classNames("page-table")} id={idContainer}>
        <ReactDragListView.DragColumn {...dragProps}>
          <Table
            {...props}
            pagination={props.pagination || false}
            showSorterTooltip={showSorterTooltip}
            className={classNames(
              "custom-scrollbar",
              { "big-checkbox-col": !expandable },
              className
            )}
            virtual={virtual}
            expandable={{
              ...props.expandable,
              onExpand: (expandable, record) => {
                if (typeof props.expandable.onExpand === "function")
                  props.expandable.onExpand(expandable, record);
                setCheckExpand(true);
              }
            }}
            columns={newColumn}
            components={colResizable ? components : undefined}

          />
        </ReactDragListView.DragColumn>

      </div>
    </>
  );
}
export default StandardTable;
